import { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EndpointKey } from '../http/EndpointKey';
import { isEndpointStatusError, isEndpointStatusSuccess, getEndpointTimings, getEndpointRetries, hasEndpointBeenFetched, getHttpResponse } from '../store/http-responses/selectors';
import { retryEndpoint } from '../store/http-responses/action-creators';
import { LayoutType } from 'signup-components/types';
import { Data } from 'signup-constants/signupData/Data';
import { getSignupDataValue } from '../store/signup-data/selectors';
import { getGuessedDataBreeze } from '../store/guessed-prefill/selectors';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { TrackWhen, useExperiment } from '../experiments/useExperiment';
import { getCopyKeyByElaspedTime } from '../hooks/breeze/getCopyKey';
import { getSecondsElapsed } from '../utils/getSecondsElapsed';
import { CopyKey, config } from '../hooks/breeze/copyKey';
import Logger from '../lib/Logger';
import { hasViewedCompanyDomain } from '../store/flow/selectors';
import { Tracker } from '../store/tracking/action-creators';
import { ExperimentParameterGroup } from '../experiments/ExperimentParameterGroup';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import { setShowBreezeErrorModal } from '../store/flow/action-creators';

// Constants
import { jsx as _jsx } from "react/jsx-runtime";
const TIMEOUT_INTERVAL = 1000; // 1 second
const MAX_ELAPSED_TIME = 10; // 10 seconds
const ERROR_THRESHOLD = 8; // 8 seconds
const SUBMIT_DELAY = 3000; // 3 seconds
const RETRY_DELAY = 1500; // 1 second
const LOADING_DURATION = 1500; // 1.5 seconds for loading state

// Non-retryable domains
const NON_RETRYABLE_DOMAINS = new Set(['hubspot.com', 'hubspotemail.net', 'hubspotemailqa.com', 'hubspot-test.com']);

// Context type

// Create context
const BreezeContext = /*#__PURE__*/createContext(undefined);

// Provider component
export const BreezeProvider = ({
  children
}) => {
  const dispatch = useDispatch();

  // State
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoadingState, setShowLoadingState] = useState(false);
  const [isLoadingPhase, setIsLoadingPhase] = useState(true);

  // Refs
  const intervalRef = useRef();
  const submitFnRef = useRef(null);
  const loadingTimerRef = useRef();

  // Selectors
  const endpointError = useSelector(isEndpointStatusError(EndpointKey.BreezeCompanyInfo));
  const endpointSuccess = useSelector(isEndpointStatusSuccess(EndpointKey.BreezeCompanyInfo));
  const timings = useSelector(state => getEndpointTimings(state, EndpointKey.BreezeCompanyInfo));
  const breezeEndpointRetries = useSelector(state => getEndpointRetries(state, EndpointKey.BreezeCompanyInfo));
  const companyDomain = useSelector(state => getSignupDataValue(state, Data.CompanyDomain));
  const breezeCompanyDomain = useSelector(state => getGuessedDataBreeze(Data.CompanyDomain)(state));
  const hasBreezeEndpointBeenFetched = useSelector(state => hasEndpointBeenFetched(state, EndpointKey.BreezeCompanyInfo));
  const breezeResponse = useSelector(state => getHttpResponse(state, EndpointKey.BreezeCompanyInfo));
  const hasViewedCompanyDomainStep = useSelector(state => hasViewedCompanyDomain(state));
  const {
    layoutType
  } = useLayoutTemplate();
  const isSidePanel = layoutType === LayoutType.SidePanel;

  // Derived state
  const isDomainChanged = breezeCompanyDomain !== companyDomain;

  // Check if we have a response with no company info
  const hasNoCompanyInfo = useMemo(() => {
    if (!breezeResponse || typeof breezeResponse !== 'object') {
      return false;
    }
    return Boolean(breezeResponse.name) === false && Array.isArray(breezeResponse.industries) && breezeResponse.industries.length === 0;
  }, [breezeResponse]);
  const {
    isAssignedToSpecificVariant: isAssignedToACQ172Variant
  } = useExperiment(ExperimentKeys.ACQ0172, TrackWhen.never);
  const isAssignedToACQ172Variants = isAssignedToACQ172Variant(ExperimentParameterGroup.Variant_B);

  // Determine if we should retry
  const shouldRetryBreeze = () => {
    return isAssignedToACQ172Variants && isDomainChanged && breezeEndpointRetries < 1 && !NON_RETRYABLE_DOMAINS.has(companyDomain);
  };
  const clearIntervalRef = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
  };
  const clearTimers = () => {
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
      loadingTimerRef.current = undefined;
    }
  };

  // Reset seconds when new timings are received
  useEffect(() => {
    if (timings !== null && timings !== void 0 && timings.timeStarted) {
      setSecondsElapsed(0);
      setIsCalculating(false);
      // Clear any existing interval when new timings are received
      clearIntervalRef();
    }
  }, [timings === null || timings === void 0 ? void 0 : timings.timeStarted]);

  // Handle timing updates
  useEffect(() => {
    if (timings === undefined || !timings.timeStarted) {
      return;
    }
    const updateSeconds = () => {
      const seconds = getSecondsElapsed(timings.timeStarted);
      if (seconds > MAX_ELAPSED_TIME) {
        setSecondsElapsed(0);
        setIsCalculating(false);
        clearIntervalRef();
        return;
      }
      setSecondsElapsed(seconds);
      if (endpointSuccess || endpointError) {
        setIsCalculating(false);
        clearIntervalRef();
        return;
      }
    };
    if (!isCalculating) {
      setIsCalculating(true);
      updateSeconds();

      // Use setInterval for more accurate timing
      intervalRef.current = setInterval(updateSeconds, TIMEOUT_INTERVAL);
    }

    // Cleanup interval on unmount or when dependencies change
    return () => {
      if (endpointError || endpointSuccess) {
        clearIntervalRef();
      }
    };
  }, [timings, isCalculating, endpointError, endpointSuccess, dispatch, breezeEndpointRetries]);

  // Handle submission after retry
  useEffect(() => {
    if (isSubmitting && hasBreezeEndpointBeenFetched && submitFnRef.current) {
      const isACQ172VariantBError = isAssignedToACQ172Variant(ExperimentParameterGroup.Variant_B) && endpointError;
      const delay = isAssignedToACQ172Variants && !isACQ172VariantBError ? RETRY_DELAY : 0;
      const timer = setTimeout(() => {
        if (submitFnRef.current) {
          submitFnRef.current();
        }
        setIsSubmitting(false);
        submitFnRef.current = null;
        clearIntervalRef();
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [isSubmitting, hasBreezeEndpointBeenFetched, isAssignedToACQ172Variants, isSidePanel, endpointError, dispatch, isAssignedToACQ172Variant]);

  // Get the appropriate copy key based on state
  const copyKey = useMemo(() => {
    // For first-time click, show loading state for 1.5 seconds
    if (!hasViewedCompanyDomainStep && isLoadingPhase) {
      return config[CopyKey.Loading];
    }
    if (endpointError) {
      if (isAssignedToACQ172Variants) {
        dispatch(setShowBreezeErrorModal(true));
      } else {
        return config[CopyKey.Error];
      }
    }
    if (endpointSuccess) {
      if (hasNoCompanyInfo) {
        return isSidePanel ? config[CopyKey.NoInfoFoundSidePanel] : config[CopyKey.NoInfoFound];
      }
      return isSidePanel ? config[CopyKey.SuccessSidePanel] : config[CopyKey.Success];
    }
    if (!timings) {
      return config[CopyKey.Default];
    }
    if (secondsElapsed > ERROR_THRESHOLD && !endpointSuccess) {
      if (isAssignedToACQ172Variants) {
        dispatch(setShowBreezeErrorModal(true));
      } else {
        return config[CopyKey.Error];
      }
    }
    return getCopyKeyByElaspedTime(secondsElapsed);
  }, [hasViewedCompanyDomainStep, isLoadingPhase, endpointError, endpointSuccess, timings, secondsElapsed, isAssignedToACQ172Variants, dispatch, hasNoCompanyInfo, isSidePanel]);

  // Helper function to handle retry logic
  const handleRetry = submitFn => {
    clearIntervalRef();
    dispatch(retryEndpoint(EndpointKey.BreezeCompanyInfo));
    dispatch(Tracker.signupInteraction('breeze-company-info-retry'));
    submitFnRef.current = submitFn;
    setIsSubmitting(true);
    setShowLoadingState(true);
    setIsLoadingPhase(false);
  };

  // Helper function to handle first-time submission
  const handleFirstTimeSubmit = submitFn => {
    // Set loading state
    setShowLoadingState(true);
    setIsLoadingPhase(true);

    // Clear any existing timers
    clearTimers();

    // After 1.5 seconds, transition from loading to success/error state
    loadingTimerRef.current = setTimeout(() => {
      setIsLoadingPhase(false);
    }, LOADING_DURATION);

    // Store the submit function to be called after 3 seconds
    submitFnRef.current = submitFn;

    // Set a timer to call the submit function after 3 seconds
    setTimeout(() => {
      if (submitFnRef.current) {
        submitFnRef.current();
        submitFnRef.current = null;
      }
      setShowLoadingState(false);
    }, SUBMIT_DELAY);
  };

  // Submit with retry if needed
  const submitWithRetry = submitFn => {
    if (isAssignedToACQ172Variants) {
      dispatch(setShowBreezeErrorModal(false));
    }

    // Handle retry case
    if (shouldRetryBreeze()) {
      handleRetry(submitFn);
      return;
    }

    // Handle first-time submission
    if (!hasViewedCompanyDomainStep) {
      handleFirstTimeSubmit(submitFn);
      return;
    }

    // For subsequent calls, just call the submit function directly
    submitFn();
  };
  const value = {
    isRunning: !hasBreezeEndpointBeenFetched,
    copyKey,
    isAssignedToACQ172Variants,
    hasBreezeEndpointBeenFetched,
    submitWithRetry,
    showLoadingState
  };
  return /*#__PURE__*/_jsx(BreezeContext.Provider, {
    value: value,
    children: children
  });
};

// Hook to use the context
export const useBreeze = () => {
  const context = useContext(BreezeContext);
  const LOG = Logger.getLogger('BreezeContext');
  if (context === undefined) {
    LOG.error('useBreeze must be used within a BreezeProvider');
    throw new Error('useBreeze must be used within a BreezeProvider');
  }
  return context;
};